/**
 * Audit Report — /finance/reports/audit-pack
 * External audit-ready pack: period summary, control checks, entry type breakdown.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldCheck, RefreshCw, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuditData {
  reportType: string;
  period: { from: string; to: string };
  generatedAt: string;
  summary: {
    totalEntries: number;
    postedEntries: number;
    draftEntries: number;
    voidedEntries: number;
    reversedEntries: number;
    postedTotal: number;
  };
  entryTypeBreakdown: { entryType: string; count: number; totalAmount: number }[];
  controlChecks: {
    unbalancedEntryCount: number;
    unbalancedEntries: { id: string; entryNumber: string; entryType: string; amount: number }[];
    overriddenEntryCount: number;
  };
  payments: { count: number; total: number };
  periodStatus: {
    status: string;
    startDate: string;
    endDate: string;
    closedAt: string | null;
    closedBy: string | null;
  } | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function thisYear(): { from: string; to: string } {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0]! };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AuditReportPage() {
  const [data, setData]       = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom]       = useState(thisYear().from);
  const [to, setTo]           = useState(thisYear().to);
  const loadedRef = useRef(false);

  const fetchData = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      const res = await api.get<AuditData>(`/finance/reports/audit-pack?from=${f}&to=${t}`);
      setData(res ?? null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchData(from, to);
  }, [fetchData, from, to]);

  const run = () => { loadedRef.current = false; fetchData(from, to); };

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">Audit Report</h1>
          <p className="text-sm text-muted-foreground">External audit-ready financial schedules</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-3 flex flex-wrap gap-2 items-center">
          <label className="text-xs text-muted-foreground">From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="text-sm border rounded px-2 py-1" />
          <label className="text-xs text-muted-foreground">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="text-sm border rounded px-2 py-1" />
          <Button size="sm" onClick={run} disabled={loading} className="ml-auto">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Generate
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading audit pack…</div>
      ) : !data ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No data available for selected period.</div>
      ) : (
        <>
          {/* Period Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {([
              { label: 'Total Entries',    val: data.summary.totalEntries,    color: '' },
              { label: 'Posted',           val: data.summary.postedEntries,   color: 'text-green-700' },
              { label: 'Draft',            val: data.summary.draftEntries,    color: 'text-orange-600' },
              { label: 'Voided',           val: data.summary.voidedEntries,   color: 'text-red-600' },
              { label: 'Reversed',         val: data.summary.reversedEntries, color: 'text-purple-600' },
              { label: 'Posted Total',     val: fmt(data.summary.postedTotal), color: 'text-blue-700' },
            ] as const).map(item => (
              <Card key={item.label}>
                <CardContent className="py-3 text-center">
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className={`text-lg font-bold ${item.color}`}>{item.val}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Control Checks */}
          <Card>
            <CardContent className="py-4">
              <p className="text-sm font-semibold mb-3">Control Checks</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    {data.controlChecks.unbalancedEntryCount === 0
                      ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                      : <AlertCircle className="h-4 w-4 text-red-500" />}
                    Unbalanced Journal Entries
                  </span>
                  <Badge variant={data.controlChecks.unbalancedEntryCount === 0 ? 'outline' : 'destructive'} className="text-xs">
                    {data.controlChecks.unbalancedEntryCount}
                  </Badge>
                </div>
                {data.controlChecks.unbalancedEntryCount > 0 && (
                  <div className="ml-6 text-xs text-muted-foreground space-y-0.5">
                    {data.controlChecks.unbalancedEntries.map(e => (
                      <div key={String(e.id)} className="flex gap-2">
                        <span className="font-mono text-red-600">{String(e.entryNumber ?? e.id)}</span>
                        <span>{String(e.entryType)}</span>
                        <span className="ml-auto tabular-nums">{fmt(Number(e.amount))}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    {data.controlChecks.overriddenEntryCount === 0
                      ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                      : <XCircle className="h-4 w-4 text-orange-500" />}
                    Entries with Override / Reversal Flags
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {data.controlChecks.overriddenEntryCount}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Entry Type Breakdown */}
          <Card>
            <CardContent className="py-4">
              <p className="text-sm font-semibold mb-3">Posted Entries by Type</p>
              {data.entryTypeBreakdown.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No posted entries in this period.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pr-4">Entry Type</TableHead>
                        <TableHead className="text-right pr-4">Count</TableHead>
                        <TableHead className="text-right">Total Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.entryTypeBreakdown.map(row => (
                        <TableRow key={row.entryType} className="border-b last:border-0 hover:bg-muted/30">
                          <TableCell className="pr-4 font-mono">{row.entryType}</TableCell>
                          <TableCell className="text-right pr-4 tabular-nums">{row.count}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(row.totalAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payments + Period Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-2">Payments Summary</p>
                <p className="text-xs text-muted-foreground">{data.payments.count} payments recorded</p>
                <p className="text-xl font-bold mt-1">{fmt(data.payments.total)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <p className="text-sm font-semibold mb-2">Period Status</p>
                {data.periodStatus ? (
                  <>
                    <Badge
                      variant={data.periodStatus.status === 'closed' ? 'secondary' : 'outline'}
                      className="text-xs mb-2"
                    >
                      {String(data.periodStatus.status).toUpperCase()}
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      {String(data.periodStatus.startDate)} — {String(data.periodStatus.endDate)}
                    </p>
                    {data.periodStatus.closedAt && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Closed: {new Date(String(data.periodStatus.closedAt)).toLocaleDateString()}
                        {data.periodStatus.closedBy ? ` by ${String(data.periodStatus.closedBy)}` : ''}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">No accounting period configured.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
